import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  UsersIcon, 
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ShieldCheckIcon,
  // CheckIcon, // Removed unused import
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useSearchParams } from 'react-router-dom'; // Import useSearchParams
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination'; // Import Pagination

interface User {
  id: number;
  username: string;
  name: string; // Virtual field from backend for display
  firstName: string; // Added for form editing
  lastName: string; // Added for form editing
  email: string;
  role: string;
  isActive: boolean;
  lastLogin: string;
}

// Interface for paginated user response
interface PaginatedUsersResponse {
  users: User[];
  totalPages: number;
  currentPage: number;
  totalCount: number;
}

interface UserFormData {
  id?: number;
  username: string;
  firstName: string; // Changed from name
  lastName: string; // Added
  email: string;
  password: string;
  confirmPassword: string;
  role: string;
  isActive: boolean;
}

interface RolePermission {
  role: string;
  permissions: {
    [key: string]: boolean;
  };
}

// Define available permissions for each section
const availablePermissions: { [key: string]: string[] } = {
  structures: ['view', 'create', 'edit', 'delete', 'price_management', 'availability_view'],
  clients: ['view', 'create', 'edit', 'delete', 'discount_management'],
  quotes: ['view', 'create', 'edit', 'delete', 'export'],
  orders: ['view', 'create', 'edit', 'delete', 'status_management'],
  workOrders: ['view', 'create', 'edit', 'delete', 'assignment'],
  contracts: ['view', 'create', 'edit', 'delete', 'generate_documents'],
  payments: ['view', 'create', 'edit', 'delete'],
  expenses: ['view', 'create', 'edit', 'delete', 'category_management'],
  files: ['view', 'upload', 'edit', 'delete'],
  reports: ['view', 'export', 'financial', 'structures', 'clients', 'orders'],
  settings: ['view', 'edit', 'user_management', 'backup']
};

// Role permission preset types
interface BaseRolePreset {
  label: string;
  description: string;
}

interface AdminRolePreset extends BaseRolePreset {
  allAccess: boolean;
  sections?: never;
  limited?: never;
  restrictions?: never;
}

interface RestrictedRolePreset extends BaseRolePreset {
  restrictions: string[];
  allAccess?: never;
  sections?: never;
  limited?: never;
}

interface SectionRolePreset extends BaseRolePreset {
  sections: string[];
  limited: string[];
  allAccess?: never;
  restrictions?: never;
}

type RolePreset = AdminRolePreset | RestrictedRolePreset | SectionRolePreset;

// Role permission presets
const rolePresets: { [key: string]: RolePreset } = {
  'Администратор': {
    label: 'Администратор',
    description: 'Полный доступ ко всем функциям системы',
    allAccess: true
  },
  'Управляющий': {
    label: 'Управляющий',
    description: 'Управление бизнес-процессами, без доступа к настройкам системы',
    restrictions: ['settings.user_management', 'settings.backup']
  },
  'Менеджер по продажам': {
    label: 'Менеджер по продажам',
    description: 'Работа с клиентами, заказами и коммерческими предложениями',
    sections: ['clients', 'files', 'quotes', 'orders', 'contracts'],
    limited: ['reports.view', 'reports.structures', 'reports.clients', 'structures.view', 'structures.availability_view']
  },
  'Технический специалист': {
    label: 'Технический специалист',
    description: 'Управление конструкциями и нарядами на выполнение работ',
    sections: ['structures', 'workOrders'],
    limited: ['orders.view', 'clients.view', 'files.view']
  },
  'Бухгалтер': {
    label: 'Бухгалтер',
    description: 'Финансовый учет и отчетность',
    sections: ['payments', 'expenses', 'reports'],
    limited: ['orders.view', 'clients.view', 'files.view', 'contracts.view']
  }
};

const UserManagement: React.FC = () => {
  // const { user } = useAuth(); // Removed unused user variable
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [rolePermissions, setRolePermissions] = useState<RolePermission[]>([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showPermissionsForm, setShowPermissionsForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [error, setError] = useState<string | null>(null); // Add error state
  
  // State for pagination and filtering
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [roleFilter, setRoleFilter] = useState(searchParams.get('role') || 'all');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all'); // 'all', 'active', 'inactive'
  const [limit] = useState(15);
  
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    firstName: '', // Changed from name
    lastName: '', // Added
    email: '',
    password: '',
    confirmPassword: '',
    role: 'Менеджер по продажам', // Default role for new user form
    isActive: true
  });
  
  // Fetch users with pagination and filters
  const fetchUsers = useCallback(async (page = currentPage, search = searchTerm, role = roleFilter, status = statusFilter) => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    if (search) params.append('search', search);
    if (role && role !== 'all') params.append('role', role);
    if (status && status !== 'all') params.append('isActive', status === 'active' ? 'true' : 'false');

    setSearchParams(params, { replace: true });

    try {
      const response = await axios.get<PaginatedUsersResponse>('/users', { params });
      // --- Log raw response ---
      console.log('fetchUsers response:', response.data);

      // --- Check if response.data.users is an array ---
      if (response.data && Array.isArray(response.data.users)) {
        setUsers(response.data.users);
        setTotalPages(response.data.totalPages ?? 1);
        setCurrentPage(response.data.currentPage ?? 1);
        setTotalCount(response.data.totalCount ?? 0);
      } else {
        console.error('fetchUsers received invalid data structure for users:', response.data);
        setUsers([]); // Set to empty array if data is invalid
        setTotalPages(1);
        setCurrentPage(1);
        setTotalCount(0);
        setError('Ошибка загрузки пользователей: неверный формат данных');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Ошибка загрузки пользователей');
      setUsers([]);
      setTotalPages(1);
      setCurrentPage(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [limit, setSearchParams, currentPage, searchTerm, roleFilter, statusFilter]);

  // Fetch role permissions (only once)
  const fetchPermissions = useCallback(async () => {
    // Consider adding loading state for permissions if needed
    try {
      const permissionsResponse = await axios.get('/settings/permissions');
       // --- Log raw response ---
      console.log('fetchPermissions response:', permissionsResponse.data);

      // --- Check if permissionsResponse.data is an array ---
      if (Array.isArray(permissionsResponse.data)) {
          setRolePermissions(permissionsResponse.data);
      } else {
          console.error('fetchPermissions received invalid data structure:', permissionsResponse.data);
          setRolePermissions([]); // Set to empty array if data is invalid
          setError('Ошибка загрузки прав доступа: неверный формат данных');
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setError('Ошибка загрузки прав доступа');
      setRolePermissions([]); // Ensure it's an empty array on error
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchUsers();
    fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount
  
  // Refetch users when filters or page change
  useEffect(() => {
    // Avoid fetching on initial mount again, already done above
    const initialLoadComplete = users.length > 0 || !loading;
    if (initialLoadComplete) {
      fetchUsers(currentPage, searchTerm, roleFilter, statusFilter);
    }
  }, [currentPage, searchTerm, roleFilter, statusFilter, fetchUsers]); // Removed loading from deps
  
  // Handle user form change
  const handleUserFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === 'checkbox';
    
    setFormData(prev => ({
      ...prev,
      [name]: isCheckbox ? (e.target as HTMLInputElement).checked : value
    }));
  };
  
  // Handle user form submit
  const handleUserFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      alert('Пароли не совпадают');
      return;
    }
    
    setSubmitting(true);
    
    try {
      if (formData.id) {
        // Update existing user
        await axios.put(`/users/${formData.id}`, formData);
      } else {
        // Create new user
        await axios.post('/users', formData);
      }
      
      // --- Correctly refresh users list using existing function ---
      // Refresh users list respecting current filters and pagination
      fetchUsers(currentPage, searchTerm, roleFilter, statusFilter); // Use fetchUsers to reload data
      
      // Reset form
      setFormData({
        username: '',
        firstName: '', // Changed from name
        lastName: '', // Added
        email: '',
        password: '',
        confirmPassword: '',
        role: 'Менеджер по продажам',
        isActive: true
      });
      
      setShowUserForm(false);
    } catch (error) {
      console.error('Error saving user:', error);
      // TODO: Show error to user in the form modal
      alert('Ошибка при сохранении пользователя'); 
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle role permission change
  const handlePermissionChange = (role: string, section: string, permission: string, value: boolean) => {
    setRolePermissions(prev => 
      prev.map(rp => {
        if (rp.role === role) {
          return {
            ...rp,
            permissions: {
              ...rp.permissions,
              [`${section}.${permission}`]: value
            }
          };
        }
        return rp;
      })
    );
  };
  
  // Apply role preset
  const applyRolePreset = (role: string) => {
    const preset = rolePresets[role as keyof typeof rolePresets];
    
    if (!preset) return;
    
    const newPermissions: {[key: string]: boolean} = {};
    
    // First, deny all permissions
    Object.keys(availablePermissions).forEach(section => {
      availablePermissions[section].forEach(permission => {
        newPermissions[`${section}.${permission}`] = false;
      });
    });
    
    // Apply preset permissions based on type
    if ('allAccess' in preset && preset.allAccess) {
      // Grant all permissions
      Object.keys(newPermissions).forEach(key => {
        newPermissions[key] = true;
      });
    } else if ('sections' in preset && Array.isArray(preset.sections)) {
      // Grant permissions for specified sections
      preset.sections.forEach((section: string) => {
        if (section in availablePermissions) {
          availablePermissions[section].forEach(permission => {
            newPermissions[`${section}.${permission}`] = true;
          });
        }
      });
      
      // Add limited permissions
      if ('limited' in preset && Array.isArray(preset.limited)) {
        preset.limited.forEach((permission: string) => {
          newPermissions[permission] = true;
        });
      }
    }
    
    // Apply restrictions
    if ('restrictions' in preset && Array.isArray(preset.restrictions)) {
      preset.restrictions.forEach((restriction: string) => {
        newPermissions[restriction] = false;
      });
    }
    
    // Update role permissions
    setRolePermissions(prev => 
      prev.map(rp => {
        if (rp.role === role) {
          return {
            ...rp,
            permissions: newPermissions
          };
        }
        return rp;
      })
    );
  };
  
  // Save permissions
  const savePermissions = async () => {
    setSubmitting(true);
    
    try {
      await axios.put('/settings/permissions', rolePermissions);
      setShowPermissionsForm(false);
    } catch (error) {
      console.error('Error saving permissions:', error);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Edit user
  const editUser = (userId: number) => {
    // Find the user in the state. The backend now includes firstName and lastName.
    const userToEdit = users.find(u => u.id === userId);
    if (userToEdit) {
      setFormData({
        id: userToEdit.id,
        username: userToEdit.username,
        firstName: userToEdit.firstName || '', // Use firstName
        lastName: userToEdit.lastName || '', // Use lastName
        email: userToEdit.email || '',
        password: '',
        confirmPassword: '',
        role: userToEdit.role,
        isActive: userToEdit.isActive
      });
      setShowUserForm(true);
    }
  };
  
  // Delete user
  const deleteUser = async (userId: number) => {
    if (window.confirm('Вы уверены, что хотите удалить этого пользователя?')) {
      try {
        await axios.delete(`/users/${userId}`);
        setUsers(users.filter(u => u.id !== userId));
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };
  
  // Handlers for list filters
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleRoleFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRoleFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const resetUserListFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setStatusFilter('all');
    setCurrentPage(1);
  };

  // Helper to get roles for filter dropdown
  const availableRoles = useMemo(() => {
    // Add safety check for rolePermissions being an array
    if (!Array.isArray(rolePermissions)) {
      console.warn('availableRoles computed when rolePermissions is not an array:', rolePermissions);
      return ['all'];
    }
    return ['all', ...rolePermissions.map(rp => rp.role)];
  }, [rolePermissions]);
  
  // Format date (consider moving to utils)
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('ru-RU', {
            year: 'numeric', month: 'numeric', day: 'numeric', 
            hour: '2-digit', minute: '2-digit' 
        }).format(date);
    } catch (e) {
        return dateString; // Return original if formatting fails
    }
  };

  // Display loading only on initial full page load
  if (loading && users.length === 0 && !error) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <PageHeader
        title="Управление пользователями"
        subtitle={`Всего пользователей: ${totalCount}`}
        actions={
          <>
            <button
              onClick={() => {
                setFormData({ /* Reset form data */
                    username: '', firstName: '', lastName: '', email: '', password: '', confirmPassword: '', // Updated reset
                    role: 'Менеджер по продажам', isActive: true
                });
                setShowUserForm(true);
                setShowPermissionsForm(false); 
              }}
              className="btn-primary flex items-center mr-2"
            >
              <PlusIcon className="h-5 w-5 mr-1" />
              Добавить пользователя
            </button>
            {/* <button
              onClick={() => { setShowPermissionsForm(true); setShowUserForm(false); }}
              className="btn-secondary flex items-center"
            >
              <ShieldCheckIcon className="h-5 w-5 mr-1" />
              Управление правами доступа
            </button> */}
          </>
        }
      />

        {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
                {error}
            </div>
        )}

       {/* Filters Section */} 
        <div className="mb-6 p-4 bg-gray-50 rounded-lg shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 items-end">
                {/* Search */} 
                <div>
                    <label htmlFor="search-users" className="form-label">Поиск</label>
                    <input
                        id="search-users"
                        type="text"
                        placeholder="Имя, логин, email..."
                        className="form-input w-full"
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                </div>
                {/* Role Filter */} 
                 <div>
                    <label htmlFor="role-filter" className="form-label">Роль</label>
                    <select
                        id="role-filter"
                        className="form-input w-full"
                        value={roleFilter}
                        onChange={handleRoleFilterChange}
                    >
                        {availableRoles.map(role => (
                            <option key={role} value={role}>
                                {role === 'all' ? 'Все роли' : role}
                            </option>
                        ))}
                    </select>
                </div>
                 {/* Status Filter */} 
                <div>
                    <label htmlFor="status-filter-users" className="form-label">Статус</label>
                    <select
                        id="status-filter-users"
                        className="form-input w-full"
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                    >
                        <option value="all">Все</option>
                        <option value="active">Активные</option>
                        <option value="inactive">Неактивные</option>
                    </select>
                </div>
                 {/* Reset Button */} 
                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={resetUserListFilters}
                        className="btn-secondary-outline inline-flex items-center text-sm"
                        disabled={loading}
                    >
                        <XMarkIcon className="h-4 w-4 mr-1"/>
                        Сбросить
                    </button>
                </div>
            </div>
        </div>

      {/* Conditional Rendering: Show forms or user list */} 
      {!showUserForm && !showPermissionsForm && (
        <>
        {loading && <LoadingSpinner />} 
        {!loading && users.length === 0 ? (
            <div className="text-center p-6 bg-white rounded-lg shadow">
                 <p className="text-gray-500">
                    {(searchTerm || roleFilter !== 'all' || statusFilter !== 'all') 
                        ? 'Пользователи, соответствующие критериям, не найдены.'
                        : 'Пользователи еще не добавлены.'}
                 </p>
                 <button
                    onClick={() => {
                         setFormData({ /* Reset form data */
                             username: '', firstName: '', lastName: '', email: '', password: '', confirmPassword: '', // Updated reset
                             role: 'Менеджер по продажам', isActive: true
                         });
                         setShowUserForm(true);
                    }}
                    className="mt-4 btn-primary inline-flex items-center"
                 >
                     <PlusIcon className="h-5 w-5 mr-1" />
                     Добавить пользователя
                 </button>
            </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Имя
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Логин
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Роль
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Статус
                    </th>
                     <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                       Последний вход
                     </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Действия
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{user.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-700">{user.username}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-700">{user.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-700">{user.role}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {user.isActive ? 'Активен' : 'Неактивен'}
                        </span>
                      </td>
                       <td className="px-6 py-4 text-sm text-gray-500">
                         {formatDate(user.lastLogin)}
                       </td>
                      <td className="px-6 py-4 text-right text-sm font-medium space-x-2">
                        <button onClick={() => editUser(user.id)} className="text-indigo-600 hover:text-indigo-900" title="Редактировать">
                          <PencilIcon className="h-5 w-5"/>
                        </button>
                        <button onClick={() => deleteUser(user.id)} className="text-red-600 hover:text-red-900" title="Удалить">
                          <TrashIcon className="h-5 w-5"/>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
         )}
          {/* Pagination */} 
         {!loading && totalPages > 1 && (
             <div className="mt-6">
                 <Pagination
                     currentPage={currentPage}
                     totalPages={totalPages}
                     onPageChange={handlePageChange}
                 />
             </div>
         )}
        </>
      )}

      {/* User Form Modal */}
      {showUserForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {formData.id ? 'Редактировать пользователя' : 'Добавить пользователя'}
              </h3>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-500"
                onClick={() => setShowUserForm(false)}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleUserFormSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                    Логин <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    required
                    value={formData.username}
                    onChange={handleUserFormChange}
                    className="form-input mt-1 block w-full"
                  />
                </div>

                {/* First Name Input */}
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                    Имя <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={handleUserFormChange}
                    className="form-input mt-1 block w-full"
                  />
                </div>

                {/* Last Name Input */}
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                    Фамилия <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={handleUserFormChange}
                    className="form-input mt-1 block w-full"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleUserFormChange}
                    className="form-input mt-1 block w-full"
                  />
                </div>
                
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Пароль {!formData.id && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    required={!formData.id}
                    value={formData.password}
                    onChange={handleUserFormChange}
                    className="form-input mt-1 block w-full"
                  />
                  {formData.id && (
                    <p className="mt-1 text-sm text-gray-500">
                      Оставьте поле пустым, чтобы не менять пароль
                    </p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Подтверждение пароля {!formData.id && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    required={!formData.id}
                    value={formData.confirmPassword}
                    onChange={handleUserFormChange}
                    className="form-input mt-1 block w-full"
                  />
                </div>
                
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                    Роль <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="role"
                    name="role"
                    required
                    value={formData.role}
                    onChange={handleUserFormChange}
                    className="form-input mt-1 block w-full"
                  >
                    <option value="Администратор">Администратор</option>
                    <option value="Управляющий">Управляющий</option>
                    <option value="Менеджер по продажам">Менеджер по продажам</option>
                    <option value="Технический специалист">Технический специалист</option>
                    <option value="Бухгалтер">Бухгалтер</option>
                  </select>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleUserFormChange}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                    Активен
                  </label>
                </div>
              </div>
              
              <div className="mt-5 flex justify-end space-x-3">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowUserForm(false)}
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Permissions Form Modal */}
      {showPermissionsForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-5xl max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Настройка ролей и разрешений
              </h3>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-500"
                onClick={() => setShowPermissionsForm(false)}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mb-4">
              <label htmlFor="roleSelect" className="block text-sm font-medium text-gray-700 mb-1">
                Выберите роль для настройки
              </label>
              <div className="flex space-x-2">
                <select
                  id="roleSelect"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="form-input block w-full"
                >
                  <option value="">Выберите роль</option>
                  {rolePermissions.map(rp => (
                    <option key={rp.role} value={rp.role}>{rp.role}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn-secondary whitespace-nowrap"
                  onClick={() => selectedRole && applyRolePreset(selectedRole)}
                  disabled={!selectedRole}
                >
                  Применить шаблон
                </button>
              </div>
              {selectedRole && rolePresets[selectedRole as keyof typeof rolePresets] && (
                <p className="mt-1 text-sm text-gray-500">
                  {rolePresets[selectedRole as keyof typeof rolePresets].description}
                </p>
              )}
            </div>
            
            {selectedRole && (
              <div className="space-y-6">
                {Object.keys(availablePermissions).map(section => (
                  <div key={section} className="border rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2 capitalize">
                      {section === 'structures' ? 'Конструкции' :
                       section === 'clients' ? 'Клиенты' :
                       section === 'quotes' ? 'Коммерческие предложения' :
                       section === 'orders' ? 'Заказы' :
                       section === 'workOrders' ? 'Наряды' :
                       section === 'contracts' ? 'Договоры' :
	                       section === 'payments' ? 'Платежи' :
	                       section === 'expenses' ? 'Расходы' :
	                       section === 'files' ? 'Файлы' :
	                       section === 'reports' ? 'Отчеты' :
	                       section === 'settings' ? 'Настройки' : section}
                    </h4>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {availablePermissions[section as keyof typeof availablePermissions].map(permission => {
                        const permissionKey = `${section}.${permission}`;
                        const rolePermission = rolePermissions.find(rp => rp.role === selectedRole);
                        const isChecked = rolePermission?.permissions[permissionKey] || false;
                        
                        return (
                          <div key={permission} className="flex items-center">
                            <input
                              type="checkbox"
                              id={permissionKey}
                              checked={isChecked}
                              onChange={(e) => handlePermissionChange(selectedRole, section, permission, e.target.checked)}
                              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                            />
                            <label htmlFor={permissionKey} className="ml-2 block text-sm text-gray-900 capitalize">
                              {permission === 'view' ? 'Просмотр' :
                               permission === 'create' ? 'Создание' :
                               permission === 'edit' ? 'Редактирование' :
                               permission === 'delete' ? 'Удаление' :
                               permission === 'upload' ? 'Загрузка' :
                               permission === 'price_management' ? 'Управление ценами' :
                               permission === 'availability_view' ? 'Просмотр доступности' :
                               permission === 'discount_management' ? 'Управление скидками' :
                               permission === 'export' ? 'Экспорт' :
                               permission === 'status_management' ? 'Управление статусами' :
                               permission === 'assignment' ? 'Назначение' :
                               permission === 'generate_documents' ? 'Генерация документов' :
                               permission === 'category_management' ? 'Управление категориями' :
                               permission === 'financial' ? 'Финансовые' :
                               permission === 'structures' ? 'По конструкциям' :
                               permission === 'clients' ? 'По клиентам' :
                               permission === 'orders' ? 'По заказам' :
                               permission === 'user_management' ? 'Управление пользователями' :
                               permission === 'backup' ? 'Резервное копирование' : permission}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowPermissionsForm(false)}
                  >
                    Отмена
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={savePermissions}
                    disabled={submitting}
                  >
                    {submitting ? 'Сохранение...' : 'Сохранить разрешения'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
